import { useEffect, useMemo, useState } from "react";
import { fetchPlatformDashboardMetrics, type PlatformDashboardMetrics } from "../../../api/platformDashboardMetrics";
import { useTenants } from "../../../api/useTenants";
import { statusConfig } from "../../../api/platformUi";
import { PLAN_ORDER, usePlanTemplatesMerged } from "../../../api/usePlanTemplatesMerged";

interface Props {
  onNavigate: (section: string) => void;
}

function formatMonthDelta(n: number, unit: string): string {
  const prefix = n > 0 ? "+" : "";
  return `${prefix}${n} ${unit}`;
}

export default function PlatformDashboard({ onNavigate }: Props) {
  const { tenants } = useTenants();
  const { mergedByPlan, loading: plansLoading } = usePlanTemplatesMerged();
  const [metrics, setMetrics] = useState<PlatformDashboardMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  useEffect(() => {
    setMetricsLoading(true);
    void fetchPlatformDashboardMetrics()
      .then(setMetrics)
      .catch(() => setMetrics(null))
      .finally(() => setMetricsLoading(false));
  }, []);

  const activeTenants = tenants.filter((t) => t.status === "active");
  const pendingTenants = tenants.filter((t) => t.status === "pending");
  const totalMembers = metrics?.totalMembers ?? tenants.reduce((s, t) => s + t.memberCount, 0);
  const totalMAU = metrics?.totalMonthlyActiveUsers ?? tenants.reduce((s, t) => s + t.monthlyActiveUsers, 0);
  const platformMAURate = totalMembers > 0 ? Math.round((totalMAU / totalMembers) * 100) : 0;
  const tn = Math.max(tenants.length, 1);

  const annualContractValue = tenants.reduce((s, t) => s + (mergedByPlan[t.plan]?.price ?? 0), 0);
  const monthlyEstimate = Math.round(annualContractValue / 12);

  const tenantTotalDisplay = metrics?.tenantTotal ?? tenants.length;
  const activeDisplay = metrics?.activeTenants ?? activeTenants.length;
  const pendingDisplay = metrics?.pendingTenants ?? pendingTenants.length;
  const statsSubtitle = metricsLoading ? "加载中" : metrics ? null : "本月增量需统计接口";

  const platformStats = [
    {
      label: "租户总数",
      value: metricsLoading ? "…" : String(tenantTotalDisplay),
      change: statsSubtitle ?? formatMonthDelta(metrics!.newTenantsThisMonth, "本月新增租户"),
      icon: "ri-building-4-line",
      color: "text-amber-600 bg-amber-50",
    },
    {
      label: "平台总会员",
      value: metricsLoading ? "…" : totalMembers.toLocaleString(),
      change: statsSubtitle ?? formatMonthDelta(metrics!.newMembersThisMonth, "本月新会员"),
      icon: "ri-group-3-line",
      color: "text-green-600 bg-green-50",
    },
    {
      label: "运营中租户",
      value: metricsLoading ? "…" : String(activeDisplay),
      change: statsSubtitle ?? `${pendingDisplay} 待激活`,
      icon: "ri-checkbox-circle-line",
      color: "text-green-600 bg-green-50",
    },
    {
      label: "套餐年费估值",
      value: plansLoading ? "…" : `¥${annualContractValue.toLocaleString()}`,
      change: `约 ¥${monthlyEstimate.toLocaleString()}/月`,
      icon: "ri-money-cny-circle-line",
      color: "text-red-600 bg-red-50",
    },
  ];

  const renewalAlerts = useMemo(() => {
    const now = Date.now();
    return tenants
      .filter((t) => {
        if (t.status === "expired") return true;
        const ex = Date.parse(t.expiresAt);
        if (Number.isNaN(ex)) return false;
        return t.status === "active" && ex < now;
      })
      .slice(0, 8);
  }, [tenants]);

  const planDistribution = PLAN_ORDER.map((key) => {
    const cfg = mergedByPlan[key];
    return {
      plan: key,
      label: cfg.label,
      count: tenants.filter((t) => t.plan === key).length,
      color: key === "premium" ? "bg-red-500" : key === "standard" ? "bg-amber-500" : key === "basic" ? "bg-green-500" : "bg-gray-400",
    };
  });

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {platformStats.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${s.color}`}>
                <i className={`${s.icon} text-lg`}></i>
              </div>
              <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                {s.change}
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-1">{s.value}</p>
            <p className="text-xs text-gray-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Secondary metrics */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 bg-white rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-4">租户套餐分布</h3>
          <div className="space-y-3">
            {planDistribution.map((p) => (
              <div key={p.plan}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${p.color}`}></span>
                    <span className="text-gray-600">{p.label}</span>
                  </span>
                  <span className="text-gray-500 font-medium">{p.count} 个租户</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${p.color} transition-all`}
                    style={{ width: `${(p.count / tn) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-50 grid grid-cols-3 gap-3">
            <div className="text-center">
              <p className="text-xl font-bold text-gray-800">{totalMembers.toLocaleString()}</p>
              <p className="text-xs text-gray-400">平台总会员</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-gray-800">{totalMAU.toLocaleString()}</p>
              <p className="text-xs text-gray-400">月活跃用户</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-amber-600">{platformMAURate}%</p>
              <p className="text-xs text-gray-400">平台平均活跃率</p>
            </div>
          </div>
        </div>

        {/* Pending & Alerts */}
        <div className="bg-white rounded-2xl p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-4">待处理事项</h3>
          <div className="space-y-3">
            {pendingTenants.map((t) => (
              <div
                key={t.id}
                onClick={() => onNavigate("tenant-list")}
                className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors"
              >
                <div className={`w-8 h-8 ${t.logoColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <i className="ri-building-4-line text-white text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{t.name}</p>
                  <p className="text-xs text-amber-700">待激活 · {t.city}</p>
                </div>
                <i className="ri-arrow-right-s-line text-gray-400 flex-shrink-0"></i>
              </div>
            ))}
            {renewalAlerts.map((t) => (
              <div
                key={`exp-${t.id}`}
                onClick={() => onNavigate("tenant-list")}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-gray-500 text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-700 truncate">{t.name}</p>
                  <p className="text-xs text-gray-400">
                    {t.status === "expired" ? "状态已过期" : "已到期，请跟进续费"} · {t.expiresAt}
                  </p>
                </div>
                <i className="ri-arrow-right-s-line text-gray-400 flex-shrink-0"></i>
              </div>
            ))}
            {pendingTenants.length === 0 && renewalAlerts.length === 0 && (
              <p className="text-xs text-gray-400 py-2">暂无待处理事项</p>
            )}
          </div>
        </div>
      </div>

      {/* Tenant Table */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            全部租户状态
          </h3>
          <button
            onClick={() => onNavigate("tenant-list")}
            className="text-xs text-amber-600 hover:text-amber-700 cursor-pointer flex items-center gap-1"
          >
            管理租户 <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">租户名称</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">租户代码</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">套餐</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">会员数</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">月活</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">状态</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">到期时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tenants.map((t) => {
                const plan = mergedByPlan[t.plan];
                const status = statusConfig[t.status];
                const mauRate = t.memberCount > 0 ? Math.round((t.monthlyActiveUsers / t.memberCount) * 100) : 0;
                return (
                  <tr
                    key={t.id}
                    onClick={() => onNavigate("tenant-list")}
                    className="hover:bg-gray-50/50 cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 ${t.logoColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <i className="ri-building-4-line text-white text-xs"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{t.name}</p>
                          <p className="text-xs text-gray-400">{t.city} · {t.sport}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">{t.code}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${plan.bgColor} ${plan.color}`}>
                        {plan.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-medium">{t.memberCount.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${mauRate >= 60 ? "text-green-600" : mauRate > 0 ? "text-amber-600" : "text-gray-400"}`}>
                        {t.monthlyActiveUsers > 0 ? `${mauRate}%` : "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${status.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{t.expiresAt}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
